import React from 'react'
import { DataTable } from 'components/DataTable'
import { createColumnHelper } from '@tanstack/react-table'
import { usdFormatterOrFallback } from 'utils/formatting'
import { DealSearchResult } from 'services/search'
import { OverflownText } from 'components/OverflownText'
import { TruncatedTextCell } from 'components/Tables/CommonTableCells/TruncatedTextCell'

// Common styles for text truncation
const TRUNCATION_STYLE = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

interface UnifiedSearchDealTableProps {
  results: DealSearchResult[]
  isLoading: boolean
  currentTotalRows: number
  pageSize: number
  currentPage: number
  setCurrentPage: (page: number) => void
  onRowClick: (deal: DealSearchResult) => void
}

export function UnifiedSearchDealTable({
  results,
  isLoading,
  currentTotalRows,
  pageSize,
  currentPage,
  setCurrentPage,
  onRowClick,
}: UnifiedSearchDealTableProps) {
  const columnHelper = createColumnHelper<DealSearchResult>()

  const columns = [
    columnHelper.accessor('name', {
      header: 'Deal Name',
      size: 220,
      cell: (info) => {
        const value = info.getValue() ?? '-'
        return (
          <div className="max-w-full">
            <OverflownText tooltipText={value} maxLines={1}>
              <div style={TRUNCATION_STYLE}>{value}</div>
            </OverflownText>
          </div>
        )
      },
    }),
    columnHelper.accessor('company_name', {
      header: 'Company',
      size: 200,
      cell: (info) => <TruncatedTextCell text={info.getValue() ?? '-'} />,
    }),
    columnHelper.accessor('identifier', {
      header: 'Deal ID',
      size: 120,
      cell: (info) => <TruncatedTextCell text={info.getValue() ?? '-'} />,
    }),
    columnHelper.display({
      id: 'product',
      header: 'Product',
      size: 180,
      cell: (info) => {
        const deal = info.row.original
        const value = deal.variant_name ?? deal.product_name ?? '-'
        return <TruncatedTextCell text={value} />
      },
    }),
    columnHelper.accessor('sales_stage_name', {
      header: 'Sales Stage',
      size: 150,
      cell: (info) => <TruncatedTextCell text={info.getValue() ?? '-'} />,
    }),
    columnHelper.accessor('total_ltv', {
      header: 'LTV',
      size: 120,
      cell: (info) => {
        const value = info.getValue()
        return usdFormatterOrFallback(value)
      },
    }),
  ]

  return (
    <DataTable<DealSearchResult>
      tableKey="unified-search-deals"
      data={results}
      columns={columns}
      loading={isLoading}
      enableRowSelection={false}
      enableColumnResizing
      rowHeight={44}
      headerHeight={40}
      virtualizeRows={false}
      isPaginationEnabled={true}
      height={430}
      autoHideScrollbar={false}
      paginationOptions={{
        totalRows: currentTotalRows,
        pageSize: pageSize,
        setPage: setCurrentPage,
        page: currentPage,
        isPaginationLoading: isLoading,
      }}
      onRowClick={(row) => onRowClick(row.original)}
    />
  )
}
